import PurchaseTracker from '@/utils/purchaseTracker';
import {logPurchaseDebug} from '@/utils/purchaseDebug';

const PURCHASE_PAYLOAD_URL = '/api/v1/order/purchase-payload';
const TRACKED_ORDERS_KEY = 'gaTrackedPurchaseOrderIds';

function readTrackedOrderIds() {
    try {
        const raw = sessionStorage.getItem(TRACKED_ORDERS_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch (error) {
        return [];
    }
}

function markOrderTracked(orderId) {
    if (!orderId) {
        return;
    }

    const ids = readTrackedOrderIds();
    const id = String(orderId);
    if (!ids.includes(id)) {
        ids.push(id);
        sessionStorage.setItem(TRACKED_ORDERS_KEY, JSON.stringify(ids.slice(-50)));
    }
}

export function wasPurchaseTrackedForOrder(orderId) {
    if (!orderId) {
        return false;
    }

    return readTrackedOrderIds().includes(String(orderId));
}

/**
 * Fetch GA purchase payload from backend (only when payment allows tracking).
 * @returns {Promise<object|null>}
 */
export async function fetchPurchasePayload(orderId) {
    if (!orderId) {
        return null;
    }

    const response = await fetch(
        `${PURCHASE_PAYLOAD_URL}?order_id=${encodeURIComponent(orderId)}`,
        {
            method: 'GET',
            headers: {Accept: 'application/json'},
        },
    ).catch(() => null);

    if (!response || !response.ok) {
        console.warn('[GA][purchase][api] purchase-payload request failed', {orderId});
        return null;
    }

    const body = await response.json().catch(() => null);
    const data = body?.data || {};

    if (!data.should_track_purchase || !data.purchase) {
        console.info('[GA][purchase][api] payload not available', {
            orderId,
            reason: data.reason || null,
            shouldTrack: !!data.should_track_purchase,
        });
        return null;
    }

    return data.purchase;
}

function readLocalPurchaseData(orderId) {
    const raw = localStorage.getItem('lastOrderData');
    if (!raw) {
        return null;
    }

    try {
        const local = JSON.parse(raw);
        if (!local) {
            return null;
        }
        if (local.orderId && String(local.orderId) !== String(orderId)) {
            return null;
        }
        return local;
    } catch (error) {
        console.warn('[GA][purchase][api] failed to parse lastOrderData', error);
        return null;
    }
}

/**
 * API payload first (works without localStorage), then lastOrderData fallback.
 * Pushes to dataLayer via PurchaseTracker. transaction_id always order_{id}.
 *
 * @returns {Promise<boolean>}
 */
export async function trackPurchaseForOrderId(orderId, {
    clearLocalStorage = true,
} = {}) {
    if (!orderId) {
        return false;
    }

    if (wasPurchaseTrackedForOrder(orderId)) {
        console.info('[GA][purchase][api] already tracked this session', {orderId});
        return false;
    }

    let purchaseData = await fetchPurchasePayload(orderId);
    let source = 'api';

    if (!purchaseData?.value || !purchaseData?.items?.length) {
        purchaseData = readLocalPurchaseData(orderId);
        source = 'localStorage';
    }

    if (!purchaseData?.value || !purchaseData?.items?.length) {
        logPurchaseDebug('API_TRACK_SKIP_NO_PAYLOAD', {orderId});
        console.warn('[GA][purchase][api] no valid purchase payload', {orderId});
        return false;
    }

    purchaseData = {
        ...purchaseData,
        orderId,
        transaction_id: `order_${orderId}`,
    };

    console.info('[GA][purchase][api] sending purchase to dataLayer', {
        orderId,
        transaction_id: purchaseData.transaction_id,
        value: purchaseData.value,
        itemsCount: purchaseData.items.length,
        source,
    });

    const tracker = window.purchaseTracker || new PurchaseTracker();
    window.purchaseTracker = tracker;
    tracker.trackPurchase(purchaseData);
    markOrderTracked(orderId);

    if (clearLocalStorage) {
        localStorage.removeItem('lastOrderData');
        localStorage.removeItem('lastFastOrderData');
    }

    logPurchaseDebug('API_TRACK_SENT', {
        orderId,
        transaction_id: purchaseData.transaction_id,
        value: purchaseData.value,
        source,
    });

    return true;
}
